import { ChevronRightIcon, DollarSignIcon, Sparkles, TrendingUpIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTranslate } from "@/utils/i18n";

interface Props {
  readonly?: boolean;
  tickerCount: Record<string, number>;
}

const TickersSection = (props: Props) => {
  const t = useTranslate();

  const tickers = Object.entries(props.tickerCount)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .sort((a, b) => b[1] - a[1]);

  return (
    <div className="w-full flex flex-col justify-start items-start h-auto shrink-0 flex-nowrap hide-scrollbar">
      {/* Section header with icon */}
      <div className="flex items-center justify-between w-full gap-1 mb-2">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/5 dark:to-emerald-950/5 border border-green-200/50 dark:border-green-800/10 flex items-center justify-center">
            <Sparkles className="w-3 h-3 text-green-600 dark:text-green-400" />
          </div>
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("tickers_section.title")}</span>
        </div>
        {tickers.length > 0 && (
          <Link
            to="/tickers"
            className="group relative px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-300 flex items-center gap-1"
          >
            {/* Background gradient on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-emerald-500/10 dark:from-green-500/5 dark:to-emerald-500/5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {/* Border gradient */}
            <div className="absolute inset-0 rounded-full border border-green-500/20 dark:border-green-500/20 group-hover:border-green-500/40 dark:group-hover:border-green-500/30 transition-colors duration-300" />

            {/* Content */}
            <span className="relative text-muted-foreground group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors duration-300">
              {t("tickers_section.view_all")}
            </span>
            <ChevronRightIcon className="relative w-3 h-3 text-muted-foreground/60 group-hover:text-green-600 dark:group-hover:text-green-400 transition-all duration-300 group-hover:translate-x-0.5" />
          </Link>
        )}
      </div>
      {tickers.length > 0 ? (
        <div className="w-full flex flex-row justify-start items-center relative flex-wrap gap-x-2 gap-y-1.5">
          {tickers.slice(0, 10).map(([ticker, amount]) => {
            return (
              <Link
                key={ticker}
                to={`/tickers/${ticker}`}
                className={cn(
                  "shrink-0 w-auto max-w-full text-sm rounded-md leading-6 flex flex-row justify-start items-center select-none cursor-pointer transition-colors",
                  "hover:bg-accent hover:text-accent-foreground",
                  "text-muted-foreground hover:opacity-80",
                )}
              >
                <DollarSignIcon className="w-4 h-auto shrink-0 text-current" />
                <span className={cn("ml-0.5", "font-medium")}>{ticker}</span>
                <span className="opacity-60 shrink-0">({amount})</span>
              </Link>
            );
          })}
          {tickers.length > 10 && (
            <Link to="/tickers" className="shrink-0 text-xs text-muted-foreground hover:text-primary transition-colors">
              {t("tickers_section.more", { count: tickers.length - 10 })}
            </Link>
          )}
        </div>
      ) : (
        !props.readonly && (
          <div className="p-2 border border-dashed rounded-md flex flex-row justify-start items-start gap-2 text-muted-foreground">
            <TrendingUpIcon className="w-5 h-5 shrink-0 text-current opacity-60" />
            <p className="text-sm leading-snug italic">{t("tickers_section.use_symbol_hint")}</p>
          </div>
        )
      )}
    </div>
  );
};

export default TickersSection;
